import { ComptabiliteSection } from './ComptabiliteSection.js';
import { Badge, Button, Checkbox, Dialog, Input, Select } from '@artosera/ui';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import {
  accesApiApi,
  useJetons,
  useEmettreJeton,
  useRevoquerJeton,
  type CorpsJeton,
  type JetonPresente,
} from '../../api/accesApi.js';
import { estApiError } from '../../api/errors.js';
import { estErreurStepUpRequis } from '../../api/coffre.js';
import { dansLaPorteeCourante } from '../../api/cles.js';
import { enregistrerFichier } from '../../api/client.js';
import { estPorteeObsolete } from '../../auth/identite.js';
import { galerieALaction } from '../../auth/sujet.js';
import { StepUpDialog } from '../coffre/StepUpDialog.js';
import { Field } from '../Field.js';
import { useShellContext } from '../shell/context.js';
import styles from './AccesApiPage.module.css';

export function AccesApiPage(): JSX.Element {
  const { compte, galerieSlug, galerie } = useShellContext();
  // Toute transition d'identité, de galerie ou de droits détruit le secret local.
  return (
    <ContenuAccesApi
      key={`${compte.id}:${galerieSlug}:${galerie.actions.join(',')}:${(galerie.modules ?? []).join(',')}`}
    />
  );
}
function ContenuAccesApi(): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const jetons = useJetons(galerieSlug);
  const emettre = useEmettreJeton(galerieSlug);
  const revoquer = useRevoquerJeton(galerieSlug);
  const modifiable = galerieALaction(galerie, 'settings:write');
  const [creation, setCreation] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const [aRevoquer, setARevoquer] = useState<JetonPresente | null>(null);
  const [elevation, setElevation] = useState<null | (() => void)>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const date = (valeur: string | null) =>
    valeur
      ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(valeur))
      : t('accesApi.jetons.jamais');
  const scope = (valeur: string) => {
    const [action, ressource] = valeur.split(':');
    return `${t(`accesApi.scopes.${ressource}`)} — ${t(`accesApi.actions.${action}`)}`;
  };
  const geste = (executer: () => Promise<unknown>) => {
    setErreur(null);
    void executer().catch((e: unknown) => {
      if (estPorteeObsolete(e)) return;
      if (estErreurStepUpRequis(e)) setElevation(() => () => geste(executer));
      else setErreur(estApiError(e) ? e.message : t('accesApi.erreurReseau'));
    });
  };
  const telecharger = () =>
    geste(async () => {
      const fichier = await dansLaPorteeCourante((appel) =>
        accesApiApi.openapi(galerieSlug, appel),
      );
      enregistrerFichier(fichier, 'artosera-openapi.json');
    });
  return (
    <div className={styles.page} data-ui="api-access">
      <header>
        <h1 className={styles.titre} data-ui="page-heading">
          {t('accesApi.titre')}
        </h1>
        <p className={styles.sousTitre}>{t('accesApi.sousTitre')}</p>
      </header>
      <section aria-labelledby="titre-jetons" className={styles.section} data-ui="section">
        <header className={styles.entete}>
          <h2 id="titre-jetons">{t('accesApi.jetons.titre')}</h2>
          {modifiable && (
            <Button
              onClick={() => {
                setErreur(null);
                setCreation(true);
              }}
            >
              {t('accesApi.jetons.creer')}
            </Button>
          )}
        </header>
        {erreur && !creation && aRevoquer === null && (
          <p role="alert" className={styles.erreur}>
            {erreur}
          </p>
        )}
        {jetons.isPending ? (
          <p>{t('commun:chargement')}</p>
        ) : jetons.isError ? (
          <p role="alert">
            {estApiError(jetons.error) ? jetons.error.message : t('accesApi.erreurReseau')}
          </p>
        ) : jetons.data.jetons.length === 0 ? (
          <p>{t('accesApi.jetons.vide')}</p>
        ) : (
          <table className={styles.table} data-ui="data-table">
            <caption className={styles.masque}>{t('accesApi.jetons.titre')}</caption>
            <thead>
              <tr>
                {['nom', 'acces', 'cree', 'usage', 'expire', 'etat', 'actions'].map((c) => (
                  <th key={c} scope="col">
                    {t(`accesApi.jetons.colonnes.${c}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {jetons.data.jetons.map((j) => (
                <tr key={j.id}>
                  <th scope="row">
                    <strong>{j.nom}</strong>
                    <code className={styles.prefixe}>{j.prefixe}…</code>
                  </th>
                  <td data-label={t('accesApi.jetons.colonnes.acces')}>
                    <div className={styles.scopes}>
                      {j.scopes.map((s) => (
                        <Badge key={s} tone="neutral">
                          {scope(s)}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td data-label={t('accesApi.jetons.colonnes.cree')}>
                    {date(j.creeLe)}
                    <small>{j.creePar}</small>
                  </td>
                  <td data-label={t('accesApi.jetons.colonnes.usage')}>{date(j.dernierUsageLe)}</td>
                  <td data-label={t('accesApi.jetons.colonnes.expire')}>{date(j.expireLe)}</td>
                  <td data-label={t('accesApi.jetons.colonnes.etat')}>
                    <Badge tone={j.etat === 'ACTIF' ? 'available' : 'neutral'}>
                      {t(`accesApi.jetons.etats.${j.etat}`)}
                    </Badge>
                  </td>
                  <td>
                    {modifiable && j.etat !== 'REVOQUE' && (
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setErreur(null);
                          setARevoquer(j);
                        }}
                      >
                        {t('accesApi.jetons.revoquer')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      <section aria-labelledby="titre-openapi" className={styles.section} data-ui="section">
        <h2 id="titre-openapi">{t('accesApi.openapi.titre')}</h2>
        <p>{t('accesApi.openapi.texte')}</p>
        <div>
          <Button variant="secondary" onClick={telecharger}>
            {t('accesApi.openapi.telecharger')}
          </Button>
        </div>
      </section>
      <ComptabiliteSection />
      {creation && (
        <DialogueCreationJeton
          scopes={jetons.data?.scopesAccordables ?? []}
          durees={jetons.data?.durees ?? [30, 90, 180, 365]}
          enCours={emettre.isPending}
          erreur={erreur}
          onFermer={() => setCreation(false)}
          onValider={(corps) =>
            geste(async () => {
              const r = await emettre.mutateAsync(corps);
              setCreation(false);
              setSecret(r.secret);
            })
          }
        />
      )}
      <DialogueSecret secret={secret} onFermer={() => setSecret(null)} />
      <Dialog
        open={aRevoquer !== null}
        onOpenChange={(o) => {
          if (!o) setARevoquer(null);
        }}
        title={t('accesApi.revocation.titre')}
        description={t('accesApi.revocation.texte')}
        closeLabel={t('commun:fermer')}
        footer={
          <Button
            variant="danger"
            disabled={revoquer.isPending}
            onClick={() => {
              if (aRevoquer)
                geste(async () => {
                  await revoquer.mutateAsync(aRevoquer.id);
                  setARevoquer(null);
                });
            }}
          >
            {t('accesApi.jetons.revoquer')}
          </Button>
        }
      >
        <p>{aRevoquer?.nom}</p>
        {erreur && (
          <p role="alert" className={styles.erreur}>
            {erreur}
          </p>
        )}
      </Dialog>
      <StepUpDialog
        open={elevation !== null}
        onOpenChange={(o) => {
          if (!o) setElevation(null);
        }}
        onVerifie={() => {
          const rejouer = elevation;
          setElevation(null);
          rejouer?.();
        }}
        description={t('accesApi.elevation')}
      />
    </div>
  );
}
function DialogueCreationJeton({
  scopes,
  durees,
  enCours,
  erreur,
  onFermer,
  onValider,
}: {
  scopes: readonly string[];
  durees: readonly number[];
  enCours: boolean;
  erreur: string | null;
  onFermer: () => void;
  onValider: (c: CorpsJeton) => void;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const [nom, setNom] = useState('');
  const [duree, setDuree] = useState('90');
  const [choisis, setChoisis] = useState<string[]>([]);
  const groupes = [...new Set(scopes.map((s) => s.split(':')[1] ?? ''))];
  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o && !enCours) onFermer();
      }}
      title={t('accesApi.creation.titre')}
      closeLabel={t('commun:fermer')}
      description={t('accesApi.creation.acces')}
    >
      {erreur && (
        <p role="alert" className={styles.erreur}>
          {erreur}
        </p>
      )}
      <form
        className={styles.formulaire}
        onSubmit={(e) => {
          e.preventDefault();
          onValider({
            nom,
            scopes: choisis,
            expireDansJours: Number(duree) as CorpsJeton['expireDansJours'],
          });
        }}
      >
        <Field label={t('accesApi.creation.nom')} htmlFor="nom-jeton">
          <Input
            id="nom-jeton"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            minLength={2}
            maxLength={120}
            required
            autoComplete="off"
          />
        </Field>
        <Field label={t('accesApi.creation.duree')} htmlFor="duree-jeton">
          <Select
            id="duree-jeton"
            value={duree}
            onValueChange={setDuree}
            options={durees.map((n) => ({
              value: String(n),
              label: t('accesApi.creation.jours', { n }),
            }))}
          />
        </Field>
        <div className={styles.groupes}>
          {groupes.map((r) => (
            <fieldset key={r}>
              <legend>{t(`accesApi.scopes.${r}`)}</legend>
              {scopes
                .filter((s) => s.endsWith(`:${r}`))
                .map((s) => (
                  <Checkbox
                    key={s}
                    id={`scope-${s}`}
                    label={`${t(`accesApi.scopes.${r}`)} — ${t(`accesApi.actions.${s.split(':')[0]}`)}`}
                    checked={choisis.includes(s)}
                    onCheckedChange={(c) =>
                      setChoisis((v) => (c === true ? [...v, s] : v.filter((x) => x !== s)))
                    }
                  />
                ))}
            </fieldset>
          ))}
        </div>
        <Button type="submit" disabled={enCours || nom.trim().length < 2 || choisis.length === 0}>
          {t('accesApi.creation.valider')}
        </Button>
      </form>
    </Dialog>
  );
}
function DialogueSecret({
  secret,
  onFermer,
}: {
  secret: string | null;
  onFermer: () => void;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const [copie, setCopie] = useState('');
  return (
    <Dialog
      open={secret !== null}
      onOpenChange={() => undefined}
      title={t('accesApi.secret.titre')}
      description={t('accesApi.secret.avertissement')}
      closeLabel={t('commun:fermer')}
      footer={
        <Button
          onClick={() => {
            setCopie('');
            onFermer();
          }}
        >
          {t('accesApi.secret.compris')}
        </Button>
      }
    >
      {secret !== null && (
        <div className={styles.formulaire}>
          <Input
            className={styles.prefixe}
            aria-label={t('accesApi.secret.titre')}
            value={secret}
            readOnly
            autoComplete="off"
            spellCheck={false}
            onFocus={(e) => e.target.select()}
          />
          <Button
            variant="secondary"
            onClick={() => {
              void navigator.clipboard
                ?.writeText(secret)
                .then(() => setCopie(t('accesApi.secret.copie')))
                .catch(() => setCopie(t('accesApi.secret.echecCopie')));
              if (!navigator.clipboard) setCopie(t('accesApi.secret.echecCopie'));
            }}
          >
            {t('accesApi.secret.copier')}
          </Button>
          <p role="status">{copie}</p>
        </div>
      )}
    </Dialog>
  );
}
